// KrakenRF / KrakenSDR-kontrakt — RF-PEILING (DoA) fra kystobservasjonspost inn i
// SKADE (NJORD-bruken: radar + RF + hydrofon + PTZ på samme post).
//
// ════════════════════════════════════════════════════════════════════════════
// HVA KRAKEN FAKTISK GIR — OG HVA DEN IKKE GIR
// ════════════════════════════════════════════════════════════════════════════
// KrakenSDR gir RETNING til en sender. Den gir IKKE avstand. Det finnes ingen
// avstandsmåling i en DoA-løsning fra en 5-elements array: fasesammenligning over
// noen desimeter apertur løser vinkel, ikke rekkevidde. Signalstyrke er heller
// ikke avstand — den er sendereffekt × antennemønster × utbredelse × fading, og
// alle fire er ukjente for oss. Derfor:
//
//   ÉN PEILING = ÉN LINJE. ALDRI ETT PUNKT.
//
// Hele fila er bygget rundt den setningen. Se `krakenPeilingTilTrack` for hvordan
// det er løst mot `Track` (som krever lat/lon), og `krysspeiling` for den ENESTE
// ærlige veien til en posisjon: skjæring mellom peilinger fra ULIKE poster.
//
// ── Verifiserte kilder (lest i kode, ikke gjettet) ─────────────────────────────
// [S1] krakenrf/krakensdr_doa, `_sdr/_signal_processing/kraken_sdr_signal_processor.py`
//      (main). Utskrift av «Kraken App»-CSV (linje ~755–784), `wr_json` (~1298),
//      `wr_xml` (~1075), `wr_kerberos` (~1139), `calculate_end_lat_lng` (~1232),
//      `calculate_doa_papr` (~1703), `estimate_DOA` (~930–1038).
// [S2] samme repo, `_ui/_web_interface/variables.py` → `DOA_METHODS`.
// [S3] samme repo, `_ui/_web_interface/views/station_config_card.py` → DOA Data Format.
// [S4] samme repo, `_ui/_web_interface/views/dsp_config_card.py` → ULA/UCA/Custom,
//      Array Offset. `views/vfo_config_card.py` → squelch-modus, 16 VFO-er.
// [S5] samme repo, `_ui/_web_interface/utils.py` → DAQ-rammetyper og sync-flagg.
// [S6] krakenrf/krakensdr_docs wiki «13. Appendix» → CSV-feltlista 1–377 ordrett.
// [S7] krakenrf.com / Crowd Supply → 5 koherente R820T2-kanaler, 24–1766 MHz,
//      maks kanalbåndbredde 2,56 MHz.
//
// Alt som IKKE er dekket av [S1]–[S7] er merket «UVERIFISERT» der det står.
import { z } from 'zod';
import { num } from './spark.js';
import { featureCollection, haversineM, project } from './geo.js';
// ════════════════════════════════════════════════════════════════════════════
// 1. MASKINVAREGRENSER — de bestemmer hva en post i det hele tatt kan høre
// ════════════════════════════════════════════════════════════════════════════
/** Nedre tuning-grense (Hz). [S7] R820T2-standard. */
export const KRAKEN_MIN_HZ = 24_000_000;
/** Øvre tuning-grense (Hz). [S7]. Merk: 2,4/5,8 GHz ligger UTENFOR. */
export const KRAKEN_MAKS_HZ = 1_766_000_000;
/** Maks koherent kanalbåndbredde (Hz). [S7]. */
export const KRAKEN_MAKS_BANDBREDDE_HZ = 2_560_000;
/** Antall koherente kanaler = antenneelementer. [S7]. */
export const KRAKEN_ELEMENTER = 5;
/** Maks antall VFO-er DSP-en støtter. [S1] `max_vfos = 16`, [S4]. */
export const KRAKEN_MAKS_VFO = 16;
/**
 * Kan en Kraken høre denne frekvensen DIREKTE? Svarer nei for 2,4/5,8 GHz — de
 * viktigste dronebåndene. Det er ikke en detalj: uten nedkonverter foran arrayen
 * finnes drone-kontrollenken rett og slett ikke i dataene, og en post som er satt
 * opp uten den vil se helt stille ut mens en USV styres 5 km unna.
 */
export function kanKrakenLytte(frekvensHz) {
    return frekvensHz >= KRAKEN_MIN_HZ && frekvensHz <= KRAKEN_MAKS_HZ;
}
// ════════════════════════════════════════════════════════════════════════════
// 2. ENUM-ER SPEILET FRA KRAKEN-KODEN (wire-verdiene beholdes ordrett)
// ════════════════════════════════════════════════════════════════════════════
/**
 * DoA-algoritme. Verdiene er ordrett fra `DOA_METHODS` [S2] — de kommer inn på
 * wire slik, og skal ikke oversettes. `.catch` gjør en ukjent (ny) algoritme til
 * MUSIC-etiketten, men den lave vekten i `PEILEFEIL_BASIS_DEG` gjelder da ikke,
 * så et ukjent navn skal helst loggføres av broen.
 */
export const KrakenDoaAlgoritme = z
    .enum(['Bartlett', 'Capon', 'MEM', 'TNA', 'MUSIC', 'ROOT-MUSIC'])
    .catch('MUSIC');
/**
 * Antennegeometri. ULA/UCA/Custom er brukervalgene [S4]; VULA er en INTERN
 * transformasjon DSP-en gjør av en UCA når ROOT-MUSIC eller dekorrelasjon er slått
 * på ([S1] `estimate_DOA`), og kan derfor dukke opp i logg/status.
 *
 * HVORFOR DETTE ER I KONTRAKTEN: en ULA (rett linje) er tvetydig framover/bakover
 * — den kan ikke skille 30° fra 150° uten at operatøren har låst retningen
 * («ULA Output Direction»). En UCA (sirkel) dekker 360°. Å behandle de to likt
 * ville gitt speilvendte kryssepeilinger.
 */
export const KrakenAntennegeometri = z.enum(['ULA', 'UCA', 'VULA', 'Custom']).catch('UCA');
/** DAQ-rammetype fra Heimdall-headeren, ordrett fra [S5]. */
export const KrakenRammetype = z
    .enum(['Data', 'Dummy', 'Calibration', 'Trigger wait', 'Empty', 'Unknown'])
    .catch('Unknown');
/** GPS-status, ordrett fra [S1] (`self.gps_status`). */
export const KrakenGpsStatus = z.enum(['Disabled', 'Connected', 'Error']).catch('Disabled');
/** Squelch-modus per VFO, ordrett fra [S4] + [S1]. */
export const KrakenSquelchModus = z
    .enum(['Manual', 'Auto', 'Auto Channel', 'Default'])
    .catch('Auto');
/**
 * Hva peilingen er MÅLT MOT. Dette er det viktigste feltet i hele fila.
 *
 * Kraken rapporterer peilingen relativt til arrayens egen referanseakse (etter
 * «Array Offset»). For å få en SANN (geografisk) peiling må postens heading
 * legges til — [S1] `calculate_end_lat_lng` gjør nøyaktig
 * `theta = my_bearing + (360 - doa)`. Uten heading er tallet ubrukelig på et kart.
 *
 * En FAST kystpost har ingen GPS-kurs (farten er null), så den MÅ ha «Fixed
 * Heading» satt til arrayens sanne referanseasimut i Kraken-GUI-et. Er den ikke
 * satt, skriver Kraken `0.0` — som er umulig å skille fra «peker rett nord».
 * Derfor tolkes heading = 0 som UKJENT som standard, ikke som nord.
 */
export const KrakenPeilingReferanse = z.enum(['sann', 'array-relativ']);
// ════════════════════════════════════════════════════════════════════════════
// 3. PEILINGEN
// ════════════════════════════════════════════════════════════════════════════
/**
 * Én DoA-observasjon, normalisert. Alt som kan mangle, mangler eksplisitt —
 * ingen nullverdier som utgir seg for å være målinger.
 */
export const KrakenPeiling = z.object({
    /** Postens id i SKADE. Utledes av Kraken `station_id` når broen ikke setter den. */
    sensorId: z.string().min(1),
    /** Kraken `station_id` ordrett (default «NOCALL» [S1]) — kildesporing. */
    stasjonId: z.string().optional(),
    /** Målingens tid, ms epoch UTC. Kraken `timestamp` er allerede ms [S1]. */
    tsMs: z.number().int().nonnegative(),
    /** GPS-tid i ms, 0 = ingen fix [S1]. */
    gpsTsMs: z.number().int().nonnegative().optional(),
    /**
     * Peiling i grader, kompasskonvensjon (0 = nord, 90 = øst), 0–360.
     * Om dette er SANN peiling eller array-relativ, står i `referanse` — les den
     * FØR du bruker tallet til noe geografisk.
     */
    peilingDeg: z.number().min(0).max(360),
    referanse: KrakenPeilingReferanse,
    /** Rå array-relativ peiling (kompasskonvensjon) — beholdes for etterprøving. */
    arrayRelativDeg: z.number().min(0).max(360),
    /** Arrayens referanseasimut som ble brukt. Udefinert = ikke kjent. */
    headingDeg: z.number().min(0).max(360).optional(),
    /**
     * Kvalitet slik Kraken faktisk regner den: PAPR i dB for DoA-spekteret,
     * `10*log10(maks/gjennomsnitt)` [S1] `calculate_doa_papr`. Wikien [S6] kaller det
     * «0-99 float» — det stemmer ikke med koden, som er ubegrenset oppover. Vi lagrer
     * den RÅ verdien her og normaliserer separat, så ingen informasjon går tapt.
     */
    paprDb: z.number(),
    /** 0–1, `paprDb / KONFIDENS_METNING_DB` klippet. Mates til `Track.confidence`. */
    konfidens: z.number().min(0).max(1),
    frekvensHz: z.number().positive(),
    /**
     * VFO-båndbredde (Hz). IKKE med i DoA-utdataene — den ligger i `settings.json`
     * (`vfo_bw`, default 12500 [S1]). Broen må hente den derfra; mangler den, er den
     * udefinert her og ikke gjettet.
     */
    bandbreddeHz: z.number().positive().optional(),
    /**
     * Signalnivå i dB RELATIVT FULL SKALA (0 dB = maks), klippet ved -100 [S1][S6].
     * Dette er IKKE dBm og ikke kalibrert — derfor heter feltet ikke `dbm`, og
     * derfor kan det aldri brukes til å anslå avstand.
     */
    signalDb: z.number().optional(),
    /** SNR i dB, om DSP-en rapporterer det ([S1] `snrs`). */
    snrDb: z.number().optional(),
    /** Antall korrelerte kilder DSP-en mener finnes ([S1] `number_of_correlated_sources`). */
    antallKorrelerteKilder: z.number().int().nonnegative().optional(),
    algoritme: KrakenDoaAlgoritme,
    antennegeometri: KrakenAntennegeometri,
    /** Postens posisjon ved målingen (Kraken kan være mobil). 0,0 = ingen fix → droppes. */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** Tid fra signal ved antenne til resultat (ms) [S6, felt 7]. Ikke nettverkslatens. */
    latencyMs: z.number().nonnegative().optional(),
    prosesseringstidMs: z.number().nonnegative().optional(),
    /** ADC-metning — peilingen er da mistenkelig ([S1] `adc_overdrive`). */
    adcOverdrive: z.boolean().optional(),
    /**
     * Hele 360-punkts DoA-spekteret i dB, indeks = grader. MERK: Kraken sender det i
     * ENHETSSIRKEL-konvensjon (0° = øst, mot klokka) [S6, felt 18–377], mens
     * `peilingDeg` er kompass. Vi roterer det til kompass ved parsing, ellers ville
     * de to feltene i samme objekt betydd ulike ting.
     */
    spektrumDb: z.array(z.number()).length(360).optional(),
});
/** PAPR-nivå (dB) som regnes som «full konfidens». UVERIFISERT: driftsvalg. */
export const KONFIDENS_METNING_DB = 20;
// ════════════════════════════════════════════════════════════════════════════
// 4. POSTENS EGEN TILSTAND
// ════════════════════════════════════════════════════════════════════════════
/**
 * Postens tilstand. Uten dette er peilingene ikke tolkbare: `headingDeg` gir
 * referanseretningen, kalibreringsflaggene sier om fasene i det hele tatt er
 * synkrone, og GPS-låsen sier om posisjonen er ekte.
 *
 * VIKTIG: kalibreringsfeltene finnes IKKE i DoA-utdataene. De ligger i
 * Heimdall-DAQ-headeren og vises i web-GUI-et [S5]. Broen må lese dem derfra
 * (`status.json` / DAQ-header) og sende dem hit. Mangler de, er de udefinert —
 * ikke `true`.
 */
export const KrakenPostTilstand = z.object({
    sensorId: z.string().min(1),
    stasjonId: z.string().optional(),
    navn: z.string().optional(),
    tsMs: z.number().int().nonnegative(),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    altM: z.number().optional(),
    /**
     * Arrayens SANNE referanseasimut (grader). For en fast post settes den i Kraken
     * som «Fixed Heading»; for en mobil kommer den fra GPS-kurs. Er den udefinert,
     * kan ingen peiling fra denne posten brukes geografisk.
     */
    headingDeg: z.number().min(0).max(360).optional(),
    /** Er heading låst (fast post) eller GPS-avledet? [S1] `fixed_heading`. */
    fastHeading: z.boolean().optional(),
    /**
     * «Array Offset» (grader): arrayens vridning i forhold til plattformens
     * forover-akse [S4]. Kraken har ALLEREDE regnet den inn i peilingen — feltet er
     * her for dokumentasjon og feilsøking, og skal IKKE legges til på nytt.
     */
    arrayOffsetDeg: z.number().optional(),
    antennegeometri: KrakenAntennegeometri,
    /** Antall elementer. Alltid 5 på KrakenSDR [S7], men leses, ikke antas. */
    elementer: z.number().int().positive().default(KRAKEN_ELEMENTER),
    /** Elementavstand i bølgelengder ([S1] `DOA_inter_elem_space`, default 0.5). */
    elementavstandLambda: z.number().positive().optional(),
    /** UCA-radius i meter ([S1] `DOA_UCA_radius_m`). */
    ucaRadiusM: z.number().positive().optional(),
    /** For ULA: retningen operatøren har låst ([S1] `ula_direction`). */
    ulaRetning: z.enum(['Forward', 'Backward', 'Both']).optional(),
    // ── Kalibrering / helse (fra DAQ-header, [S5]) ────────────────────────────
    rammetype: KrakenRammetype.optional(),
    /** Rammesynk-ord OK. */
    rammeSynk: z.boolean().optional(),
    /** Sample-delay-synk fullført (`delay_sync_flag`). */
    forsinkelseSynk: z.boolean().optional(),
    /** IQ amplitude/fase-synk fullført (`iq_sync_flag`). */
    iqSynk: z.boolean().optional(),
    /** Støykilden er PÅ = posten kalibrerer og peiler IKKE på virkelige signaler. */
    stoykildePa: z.boolean().optional(),
    adcOverdrive: z.boolean().optional(),
    gpsStatus: KrakenGpsStatus.optional(),
    /** Ekte GPS-lås. Utledet: `gpsStatus === 'Connected'` OG posisjon ≠ 0,0. */
    gpsLaas: z.boolean().optional(),
    senterfrekvensHz: z.number().positive().optional(),
    samplerateSps: z.number().positive().optional(),
    algoritme: KrakenDoaAlgoritme.optional(),
    programvareversjon: z.string().optional(),
});
/**
 * Er posten i stand til å produsere en BRUKBAR peiling akkurat nå? Returnerer
 * begrunnelsene, ikke bare en bool — operatøren skal se HVORFOR posten er ute.
 * Tom liste = klar.
 */
export function krakenPostMangler(t) {
    const m = [];
    if (t.lat === undefined || t.lon === undefined)
        m.push('mangler posisjon');
    if (t.headingDeg === undefined)
        m.push('mangler referanseretning (heading)');
    if (t.iqSynk === false)
        m.push('IQ-synk ikke fullført');
    if (t.forsinkelseSynk === false)
        m.push('sample-delay-synk ikke fullført');
    if (t.rammeSynk === false)
        m.push('rammesynk tapt');
    if (t.stoykildePa === true)
        m.push('støykilde PÅ (kalibrerer)');
    if (t.adcOverdrive === true)
        m.push('ADC-metning');
    if (t.rammetype === 'Empty')
        m.push('tomme DAQ-rammer (sannsynlig feil)');
    if (t.rammetype === 'Calibration')
        m.push('kalibreringsrammer, ikke data');
    return m;
}
// ════════════════════════════════════════════════════════════════════════════
// 5. FREKVENSOVERVÅKING OG VAKTLISTE
// ════════════════════════════════════════════════════════════════════════════
export const KrakenVaktKategori = z.enum([
    'drone-kontroll',
    'drone-video',
    'drone-telemetri',
    'usv-kontroll',
    'satkom',
    'marin-vhf',
    'ais',
    'gnss',
    'annet',
]);
/** ASCII-verdier med vilje (samme valg som `SparkMode` i spark.ts). */
export const KrakenTrusselnivaa = z.enum(['hoy', 'middels', 'lav', 'info']);
export const KrakenVaktOppforing = z.object({
    id: z.string().min(1),
    navn: z.string().min(1),
    fraHz: z.number().positive(),
    tilHz: z.number().positive(),
    kategori: KrakenVaktKategori,
    trussel: KrakenTrusselnivaa,
    /** Krever nedkonverter foran arrayen (utenfor 24–1766 MHz). Utledes, se under. */
    kreverNedkonverter: z.boolean().default(false),
    /** Minste signalnivå (dB FS) før treffet meldes. Udefinert = alt meldes. */
    minSignalDb: z.number().optional(),
    notat: z.string().optional(),
});
function vakt(id, navn, fraHz, tilHz, kategori, trussel, notat) {
    // Nedkonverter-behovet er en FØLGE av båndkantene og maskinvaregrensa — det
    // skrives ikke inn for hånd, så det aldri kan bli feil i tabellen.
    return {
        id,
        navn,
        fraHz,
        tilHz,
        kategori,
        trussel,
        kreverNedkonverter: !kanKrakenLytte(fraHz) || !kanKrakenLytte(tilHz),
        ...(notat ? { notat } : {}),
    };
}
/**
 * Startvaktliste for en norsk kystpost. Båndkantene er offentlige
 * frekvensallokeringer (ITU region 1 / ISM / SRD), ikke etterretning, og skal
 * justeres lokalt av operatøren — de er et utgangspunkt, ikke en sannhet.
 *
 * Merk hvor mange av de mest relevante bånd som har `kreverNedkonverter: true`.
 * Det er den viktigste operative innsikten i denne tabellen.
 */
export const STANDARD_VAKTLISTE = [
    vakt('ism-433', 'ISM 433 MHz', 433_050_000, 434_790_000, 'drone-telemetri', 'middels', 'Billige RC-/telemetrilenker, LoRa. Innenfor Kraken direkte.'),
    vakt('srd-868', 'SRD 868 MHz', 868_000_000, 868_600_000, 'drone-telemetri', 'middels', 'EU SRD. Vanlig telemetri på hjemmebygde USV-er.'),
    vakt('ism-915', 'ISM 902–928 MHz', 902_000_000, 928_000_000, 'drone-kontroll', 'hoy', 'ExpressLRS/Dragonlink-klassen. Lang rekkevidde, lav effekt.'),
    vakt('fpv-1g2', 'Analog FPV-video 1,2/1,3 GHz', 1_080_000_000, 1_360_000_000, 'drone-video', 'hoy', 'Analog videonedlenke. Kontinuerlig bærer = svært peilbar.'),
    vakt('marin-vhf', 'Marin VHF', 156_000_000, 162_025_000, 'marin-vhf', 'info', 'Kontekst: normal sjøtrafikk. Ikke en trussel i seg selv.'),
    vakt('ais', 'AIS 1/2', 161_950_000, 162_050_000, 'ais', 'info', 'Sammenlign med AIS-sporene: en RF-peiling uten AIS-spor er interessant.'),
    vakt('gnss-l1', 'GNSS L1', 1_574_000_000, 1_577_000_000, 'gnss', 'hoy', 'Sterk sender her er en jammer/spoofer, ikke en navigasjonsbruker.'),
    vakt('iridium', 'Iridium L-bånd', 1_616_000_000, 1_626_500_000, 'satkom', 'middels', 'Satkom-styrt USV omgår all lokal kontrollenke.'),
    vakt('ism-2g4', 'ISM 2,4 GHz', 2_400_000_000, 2_483_500_000, 'drone-kontroll', 'hoy', 'Hovedbåndet for drone-/USV-kontroll. KREVER nedkonverter.'),
    vakt('unii-5g2', 'U-NII 5,1–5,3 GHz', 5_150_000_000, 5_350_000_000, 'drone-video', 'hoy', 'Nyere digitale videolenker. KREVER nedkonverter.'),
    vakt('ism-5g8', 'ISM 5,8 GHz', 5_725_000_000, 5_875_000_000, 'drone-video', 'hoy', 'Digital videonedlenke. KREVER nedkonverter.'),
];
/** Alle vaktliste-treff for en frekvens (bånd kan overlappe — derfor en liste). */
export function vaktlisteTreff(frekvensHz, vaktliste = STANDARD_VAKTLISTE) {
    if (!Number.isFinite(frekvensHz))
        return [];
    return vaktliste.filter((v) => frekvensHz >= v.fraHz && frekvensHz <= v.tilHz);
}
/**
 * Vaktliste-treff for en peiling, med signalterskel. Returnerer det ALVORLIGSTE
 * treffet først, så et UI kan ta `[0]` uten å sortere selv.
 */
export function peilingVaktTreff(p, vaktliste = STANDARD_VAKTLISTE) {
    const rang = { hoy: 0, middels: 1, lav: 2, info: 3 };
    return vaktlisteTreff(p.frekvensHz, vaktliste)
        .filter((v) => v.minSignalDb === undefined || (p.signalDb ?? -Infinity) >= v.minSignalDb)
        .sort((a, b) => rang[a.trussel] - rang[b.trussel]);
}
/** Ett bånd posten faktisk lytter på (én VFO). */
export const KrakenVfo = z.object({
    indeks: z.number().int().min(0).max(KRAKEN_MAKS_VFO - 1),
    frekvensHz: z.number().positive(),
    bandbreddeHz: z.number().positive(),
    squelchModus: KrakenSquelchModus.optional(),
    /** Squelch-terskel i dB FS ([S1] default -120). */
    squelchDb: z.number().optional(),
    /** Har VFO-en signal over squelch nå? */
    aktiv: z.boolean().optional(),
});
/** Hva posten overvåker akkurat nå. */
export const KrakenFrekvensovervaking = z.object({
    sensorId: z.string().min(1),
    tsMs: z.number().int().nonnegative(),
    senterfrekvensHz: z.number().positive(),
    samplerateSps: z.number().positive().optional(),
    vfoer: z.array(KrakenVfo).max(KRAKEN_MAKS_VFO),
    /** Vaktlista posten er satt opp mot. Tom = bruk `STANDARD_VAKTLISTE`. */
    vaktliste: z.array(KrakenVaktOppforing).default([]),
});
/**
 * Hvilke vaktliste-oppføringer som IKKE er dekket av noen VFO — dvs. bånd vi sier
 * vi vokter, men faktisk ikke lytter på. Blindsonene er verdt mer enn dekninga:
 * en tom trefflogg fra et bånd ingen lytter på ser ut som «rolig».
 */
export function vaktlisteBlindsoner(o) {
    const liste = o.vaktliste.length ? o.vaktliste : STANDARD_VAKTLISTE;
    return liste.filter((v) => !o.vfoer.some((f) => {
        const fra = f.frekvensHz - f.bandbreddeHz / 2;
        const til = f.frekvensHz + f.bandbreddeHz / 2;
        return til >= v.fraHz && fra <= v.tilHz;
    }));
}
// ════════════════════════════════════════════════════════════════════════════
// 6. PEILEFEIL — hvor mye er én peiling verdt?
// ════════════════════════════════════════════════════════════════════════════
/**
 * Nominell 1σ peilefeil per algoritme (grader).
 *
 * UVERIFISERT — dette er driftsmessig innstilling, ikke leverandørdata. KrakenRF
 * publiserer ingen nøyaktighetsspesifikasjon, og feilen avhenger i praksis av
 * flerveis, arrayoppsett og SNR. Rangeringen er likevel begrunnet i metodene:
 * Bartlett er en ren strålformer med bredde satt av aperturen; Capon/MEM/TNA er
 * adaptive og skarpere; MUSIC/ROOT-MUSIC er delromsmetoder med superoppløsning.
 * Tallene SKAL kalibreres mot faktiske forsøk på posten før de brukes til
 * målfatning. Bytt dem her — ett sted.
 */
export const PEILEFEIL_BASIS_DEG = {
    Bartlett: 8,
    Capon: 4,
    MEM: 5,
    TNA: 5,
    MUSIC: 3,
    'ROOT-MUSIC': 3,
};
/**
 * Geometrifaktor. UVERIFISERT (samme forbehold). ULA straffes fordi
 * framover/bakover-tvetydigheten og den dårlige oppløsningen langs aksen gjør
 * ytterkantene av sektoren mye verre enn midten.
 */
export const PEILEFEIL_GEOMETRIFAKTOR = {
    UCA: 1.0,
    VULA: 1.1,
    Custom: 1.3,
    ULA: 1.5,
};
/** Maks 1σ vi noen gang rapporterer — over dette er peilingen ikke en peiling. */
export const PEILEFEIL_MAKS_DEG = 45;
/**
 * 1σ peilefeil for én peiling: algoritme × geometri × konfidens. Lav PAPR betyr
 * et flatt DoA-spekter, altså at toppen så vidt stikker opp — da er feilen mye
 * større enn nominelt, og faktoren går mot 3.
 */
export function peilefeilSigmaDeg(p) {
    const basis = PEILEFEIL_BASIS_DEG[p.algoritme] ?? 5;
    const geo = PEILEFEIL_GEOMETRIFAKTOR[p.antennegeometri] ?? 1.3;
    const k = Math.min(1, Math.max(0, p.konfidens));
    const konfidensfaktor = 1 + 2 * (1 - k);
    return Math.min(PEILEFEIL_MAKS_DEG, basis * geo * konfidensfaktor);
}
// ════════════════════════════════════════════════════════════════════════════
// 7. VINKELREGNING (0/360-wrap gjøres ett sted, aldri ad hoc)
// ════════════════════════════════════════════════════════════════════════════
/** Normaliser grader til [0, 360). */
export function normaliserGrader(d) {
    if (!Number.isFinite(d))
        return 0;
    return ((d % 360) + 360) % 360;
}
/** Fortegnsriktig differanse a − b i (−180, 180]. */
export function peilingDiffDeg(a, b) {
    let d = normaliserGrader(a) - normaliserGrader(b);
    if (d > 180)
        d -= 360;
    if (d <= -180)
        d += 360;
    return d;
}
/**
 * Kryssvinkel mellom to peilingsretninger, spiss, [0, 90]. 90 = perpendikulært
 * (best mulig kryssepeiling), 0 = parallelt (ingen løsning).
 * MERK: 5° og 175° gir SAMME kryssvinkel (5°) — to linjer nesten på samme akse er
 * like ubrukelige uansett hvilken vei de peker.
 */
export function kryssvinkelDeg(a, b) {
    const d = Math.abs(peilingDiffDeg(a, b));
    return d > 90 ? 180 - d : d;
}
/** Array-relativ peiling + arrayens referanseasimut → sann peiling. */
export function sannPeilingDeg(arrayRelativDeg, headingDeg) {
    return normaliserGrader(arrayRelativDeg + headingDeg);
}
// ── Sfærisk vektorregning. Samme jordmodell (R = 6 371 000 m) som geo.ts. ────
const JORD_R = 6_371_000;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
function tilVektor(p) {
    const f = p.lat * D2R;
    const l = p.lon * D2R;
    return [Math.cos(f) * Math.cos(l), Math.cos(f) * Math.sin(l), Math.sin(f)];
}
function fraVektor(v) {
    return {
        lat: Math.atan2(v[2], Math.hypot(v[0], v[1])) * R2D,
        lon: Math.atan2(v[1], v[0]) * R2D,
    };
}
/** Tangential enhetsvektor i retning `peilingDeg` (kompass) i punktet `p`. */
function retningsvektor(p, peilingDeg) {
    const f = p.lat * D2R;
    const l = p.lon * D2R;
    const t = normaliserGrader(peilingDeg) * D2R;
    // Lokal øst og nord i ECEF.
    const oe = [-Math.sin(l), Math.cos(l), 0];
    const on = [-Math.sin(f) * Math.cos(l), -Math.sin(f) * Math.sin(l), Math.cos(f)];
    const c = Math.cos(t);
    const s = Math.sin(t);
    return [on[0] * c + oe[0] * s, on[1] * c + oe[1] * s, on[2] * c + oe[2] * s];
}
function kryss(a, b) {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function prikk(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function normaliserVektor(v) {
    const n = Math.hypot(v[0], v[1], v[2]);
    if (!(n > 1e-12))
        return null;
    return [v[0] / n, v[1] / n, v[2] / n];
}
/** Initiell storsirkel-asimut fra a til b (grader, kompass). */
function initialPeilingDeg(a, b) {
    const f1 = a.lat * D2R;
    const f2 = b.lat * D2R;
    const dl = (b.lon - a.lon) * D2R;
    const y = Math.sin(dl) * Math.cos(f2);
    const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dl);
    return normaliserGrader(Math.atan2(y, x) * R2D);
}
// Lokalt tangentplan (øst/nord i meter) om et referansepunkt. Brukes BARE som
// parametrisering i minste-kvadraters-utjevningen; residualene regnes sfærisk, så
// løsningen konvergerer mot det riktige punktet selv om jacobianen er tilnærmet.
function tilLokal(ref, p) {
    const mPerGradLat = D2R * JORD_R;
    const mPerGradLon = mPerGradLat * Math.cos(ref.lat * D2R);
    let dlon = p.lon - ref.lon;
    if (dlon > 180)
        dlon -= 360;
    if (dlon < -180)
        dlon += 360;
    return { e: dlon * mPerGradLon, n: (p.lat - ref.lat) * mPerGradLat };
}
function fraLokal(ref, e, n) {
    const mPerGradLat = D2R * JORD_R;
    const mPerGradLon = mPerGradLat * Math.cos(ref.lat * D2R);
    return {
        lat: ref.lat + n / mPerGradLat,
        lon: ref.lon + (mPerGradLon > 1e-6 ? e / mPerGradLon : 0),
    };
}
/** Løs 2×2. Null når systemet er singulært (parallelle peilinger). */
function loes2x2(a11, a12, a21, a22, b1, b2) {
    const det = a11 * a22 - a12 * a21;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-20)
        return null;
    return [(b1 * a22 - b2 * a12) / det, (a11 * b2 - a21 * b1) / det];
}
// ════════════════════════════════════════════════════════════════════════════
// 8. PARSING — det ekte formatet OG en flat form
// ════════════════════════════════════════════════════════════════════════════
/**
 * Feltindekser i «Kraken App»-CSV-en fra `IP:8081/DOA_value.html`.
 * Ordrett fra [S6] (1-basert der) omregnet til 0-basert, og kryssjekket mot
 * utskriftskoden i [S1].
 */
export const KRAKEN_CSV_FELT = {
    tsMs: 0,
    /** `360 - theta_0` — kompasskonvensjon, men ARRAY-RELATIV. */
    arrayRelativDeg: 1,
    papr: 2,
    signalDb: 3,
    frekvensHz: 4,
    antennegeometri: 5,
    latencyMs: 6,
    stasjonId: 7,
    lat: 8,
    lon: 9,
    gpsHeadingDeg: 10,
    kompassHeadingDeg: 11,
    headingKilde: 12,
    spektrumStart: 17,
};
/** Antall felt i en komplett CSV-linje (17 + 360) [S6]. */
export const KRAKEN_CSV_ANTALL_FELT = 377;
/** Minste antall felt vi godtar (uten spekteret). */
export const KRAKEN_CSV_MIN_FELT = 13;
function tallEllerUndef(v) {
    const r = num.safeParse(v);
    return r.success ? r.data : undefined;
}
/** 0,0 fra Kraken betyr «ingen GPS-fix» [S1], ikke Atlanterhavet utenfor Ghana. */
function posEllerUndef(lat, lon) {
    if (lat === undefined || lon === undefined)
        return {};
    if (Math.abs(lat) < 1e-9 && Math.abs(lon) < 1e-9)
        return {};
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180)
        return {};
    return { lat, lon };
}
function loesReferanse(arrayRelativDeg, headingDeg, opts) {
    const rel = normaliserGrader(arrayRelativDeg);
    const harHeading = headingDeg !== undefined &&
        Number.isFinite(headingDeg) &&
        (Math.abs(headingDeg) > 1e-9 || opts.headingNullErNord === true);
    if (!harHeading) {
        // Ærlig degradering: tallet beholdes, men merkes som ubrukelig geografisk.
        return { peilingDeg: rel, referanse: 'array-relativ', arrayRelativDeg: rel };
    }
    const h = normaliserGrader(headingDeg);
    return {
        peilingDeg: sannPeilingDeg(rel, h),
        referanse: 'sann',
        arrayRelativDeg: rel,
        headingDeg: h,
    };
}
/**
 * Roter Krakens 360-punkts spekter fra enhetssirkel (0° = øst, mot klokka) til
 * kompass (0° = nord, med klokka), så indeks betyr det samme som `peilingDeg`.
 * Kompass = (360 − enhetssirkel) mod 360, samme transform som Kraken bruker på
 * selve toppen ([S1] `360 - theta_0`).
 */
function spektrumTilKompass(enhetssirkel) {
    const ut = new Array(360);
    for (let i = 0; i < 360; i++)
        ut[normaliserGrader(360 - i)] = enhetssirkel[i];
    return ut;
}
function bygg(felt, opts) {
    if (!(felt.frekvensHz > 0) || !Number.isFinite(felt.arrayRelativDeg))
        return null;
    const pos = posEllerUndef(felt.lat, felt.lon);
    const kandidat = {
        sensorId: opts.sensorId ?? felt.sensorId,
        ...(felt.stasjonId ? { stasjonId: felt.stasjonId } : {}),
        tsMs: Math.max(0, Math.round(felt.tsMs)),
        ...(felt.gpsTsMs !== undefined ? { gpsTsMs: Math.max(0, Math.round(felt.gpsTsMs)) } : {}),
        ...loesReferanse(felt.arrayRelativDeg, felt.headingDeg, opts),
        paprDb: felt.papr,
        konfidens: Math.min(1, Math.max(0, felt.papr / KONFIDENS_METNING_DB)),
        frekvensHz: felt.frekvensHz,
        ...(opts.bandbreddeHz !== undefined ? { bandbreddeHz: opts.bandbreddeHz } : {}),
        ...(felt.signalDb !== undefined ? { signalDb: felt.signalDb } : {}),
        ...(felt.snrDb !== undefined ? { snrDb: felt.snrDb } : {}),
        ...(felt.antallKorrelerteKilder !== undefined
            ? { antallKorrelerteKilder: felt.antallKorrelerteKilder }
            : {}),
        algoritme: KrakenDoaAlgoritme.parse(opts.algoritme ?? 'MUSIC'),
        antennegeometri: KrakenAntennegeometri.parse(felt.antennegeometri),
        ...pos,
        ...(felt.latencyMs !== undefined ? { latencyMs: felt.latencyMs } : {}),
        ...(felt.prosesseringstidMs !== undefined
            ? { prosesseringstidMs: felt.prosesseringstidMs }
            : {}),
        ...(felt.adcOverdrive !== undefined ? { adcOverdrive: felt.adcOverdrive } : {}),
        ...(opts.taMedSpektrum && felt.spektrum?.length === 360
            ? { spektrumDb: spektrumTilKompass(felt.spektrum) }
            : {}),
    };
    const r = KrakenPeiling.safeParse(kandidat);
    return r.success ? r.data : null;
}
function parseCsvLinje(linje, opts) {
    const f = linje.split(',').map((s) => s.trim());
    if (f.length < KRAKEN_CSV_MIN_FELT)
        return null;
    const F = KRAKEN_CSV_FELT;
    const rel = tallEllerUndef(f[F.arrayRelativDeg]);
    const frek = tallEllerUndef(f[F.frekvensHz]);
    if (rel === undefined || frek === undefined)
        return null;
    // Felt 12 sier hvilken headingsensor som gjelder [S6]. Kraken skriver i praksis
    // alltid «GPS» og samme tall i felt 10 og 11 [S1], men vi følger kontrakten.
    const kilde = (f[F.headingKilde] ?? '').toLowerCase();
    const heading = kilde === 'compass'
        ? tallEllerUndef(f[F.kompassHeadingDeg]) ?? tallEllerUndef(f[F.gpsHeadingDeg])
        : tallEllerUndef(f[F.gpsHeadingDeg]);
    let spektrum;
    if (f.length >= KRAKEN_CSV_ANTALL_FELT) {
        const s = f.slice(F.spektrumStart, F.spektrumStart + 360).map((v) => Number(v));
        if (s.length === 360 && s.every((v) => Number.isFinite(v)))
            spektrum = s;
    }
    const stasjon = f[F.stasjonId] || '';
    return bygg({
        sensorId: stasjon || 'kraken',
        ...(stasjon ? { stasjonId: stasjon } : {}),
        tsMs: tallEllerUndef(f[F.tsMs]) ?? 0,
        arrayRelativDeg: rel,
        ...(heading !== undefined ? { headingDeg: heading } : {}),
        papr: tallEllerUndef(f[F.papr]) ?? 0,
        frekvensHz: frek,
        ...(tallEllerUndef(f[F.signalDb]) !== undefined
            ? { signalDb: tallEllerUndef(f[F.signalDb]) }
            : {}),
        antennegeometri: f[F.antennegeometri],
        ...(tallEllerUndef(f[F.lat]) !== undefined ? { lat: tallEllerUndef(f[F.lat]) } : {}),
        ...(tallEllerUndef(f[F.lon]) !== undefined ? { lon: tallEllerUndef(f[F.lon]) } : {}),
        ...(tallEllerUndef(f[F.latencyMs]) !== undefined
            ? { latencyMs: tallEllerUndef(f[F.latencyMs]) }
            : {}),
        ...(spektrum ? { spektrum } : {}),
    }, opts);
}
/**
 * Parse ÉN DoA-observasjon fra hva som helst Kraken (eller broen vår) kan sende:
 *
 *  a) «Kraken App»-CSV-linje (streng) — `IP:8081/DOA_value.html`, 377 felt [S6].
 *  b) «Kraken Pro»-JSON (objekt) — POST til `127.0.0.1:8042/doapost` [S1] `wr_json`.
 *  c) «Full POST»-JSON (objekt) — små bokstaver, andre nøkkelnavn [S1].
 *  d) flat SKADE-form — `{ sensorId, peilingDeg, frekvensHz, … }`.
 *
 * FELLE SOM ER HÅNDTERT: CSV-en inneholder `360 - theta_0` (kompassretning),
 * mens JSON/XML inneholder RÅ `theta_0` i enhetssirkel-konvensjon [S1]. De to
 * formatene ser ut som samme tall og betyr motsatt rotasjonsretning. Vi
 * konverterer i parseren, ikke i klienten.
 *
 * Returnerer null i stedet for å kaste — én råtten linje skal ikke stoppe strømmen.
 */
export function parseKrakenDoa(raw, opts = {}) {
    if (typeof raw === 'string') {
        const linje = raw.split('\n').find((l) => l.trim().length > 0);
        return linje ? parseCsvLinje(linje, opts) : null;
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const o = raw;
    // (b)/(c): `radioBearing` / `radiobearing` er RÅ theta_0 (enhetssirkel).
    const raaTheta = tallEllerUndef(o['radioBearing'] ?? o['radiobearing']);
    if (raaTheta !== undefined) {
        const doaStr = o['doaArray'] ?? o['doaarray'];
        let spektrum;
        if (typeof doaStr === 'string') {
            const s = doaStr
                .split(',')
                .map((v) => v.trim())
                .filter((v) => v.length > 0)
                .map((v) => Number(v));
            if (s.length === 360 && s.every((v) => Number.isFinite(v)))
                spektrum = s;
        }
        const stasjon = String(o['station_id'] ?? o['id'] ?? '') || '';
        const lat = tallEllerUndef(o['latitude'] ?? o['lat']);
        const lon = tallEllerUndef(o['longitude'] ?? o['lng'] ?? o['lon']);
        const overdrive = o['adc_overdrive'];
        return bygg({
            sensorId: stasjon || 'kraken',
            ...(stasjon ? { stasjonId: stasjon } : {}),
            tsMs: tallEllerUndef(o['tStamp'] ?? o['time']) ?? 0,
            ...(tallEllerUndef(o['gps_timestamp']) !== undefined
                ? { gpsTsMs: tallEllerUndef(o['gps_timestamp']) }
                : {}),
            // 360 − theta_0: enhetssirkel (mot klokka) → kompass (med klokka).
            arrayRelativDeg: normaliserGrader(360 - raaTheta),
            ...(tallEllerUndef(o['gpsBearing'] ?? o['gpsheading']) !== undefined
                ? { headingDeg: tallEllerUndef(o['gpsBearing'] ?? o['gpsheading']) }
                : {}),
            papr: tallEllerUndef(o['conf']) ?? 0,
            frekvensHz: tallEllerUndef(o['freq']) ?? 0,
            ...(tallEllerUndef(o['power']) !== undefined
                ? { signalDb: tallEllerUndef(o['power']) }
                : {}),
            ...(tallEllerUndef(o['snr_db']) !== undefined ? { snrDb: tallEllerUndef(o['snr_db']) } : {}),
            ...(tallEllerUndef(o['num_corr_sources']) !== undefined
                ? { antallKorrelerteKilder: Math.max(0, Math.round(tallEllerUndef(o['num_corr_sources']))) }
                : {}),
            antennegeometri: o['antType'] ?? o['anttype'],
            ...(lat !== undefined ? { lat } : {}),
            ...(lon !== undefined ? { lon } : {}),
            ...(tallEllerUndef(o['latency']) !== undefined
                ? { latencyMs: tallEllerUndef(o['latency']) }
                : {}),
            ...(tallEllerUndef(o['processing_time']) !== undefined
                ? { prosesseringstidMs: tallEllerUndef(o['processing_time']) }
                : {}),
            ...(typeof overdrive === 'boolean'
                ? { adcOverdrive: overdrive }
                : typeof overdrive === 'string'
                    ? { adcOverdrive: overdrive.toLowerCase() === 'true' }
                    : {}),
            ...(spektrum ? { spektrum } : {}),
        }, opts);
    }
    // (d) flat SKADE-form. `referanse` styres av om heading er oppgitt, som ellers —
    // med ett unntak: sier avsenderen EKSPLISITT `referanse: 'sann'`, tror vi på det.
    const rel = tallEllerUndef(o['arrayRelativDeg'] ?? o['peilingDeg'] ?? o['bearing_deg']);
    const frek = tallEllerUndef(o['frekvensHz'] ?? o['freq_hz'] ?? o['freq']);
    if (rel === undefined || frek === undefined)
        return null;
    const sid = String(o['sensorId'] ?? o['sensor_id'] ?? o['station_id'] ?? '') || '';
    // Er `peilingDeg` gitt direkte OG referansen sagt å være sann, godtar vi det
    // uten heading — men bare når avsenderen sier det EKSPLISITT.
    const eksplisittSann = o['referanse'] === 'sann';
    const heading = tallEllerUndef(o['headingDeg'] ?? o['heading']);
    const p = bygg({
        sensorId: sid || 'kraken',
        ...(sid ? { stasjonId: sid } : {}),
        tsMs: tallEllerUndef(o['tsMs'] ?? o['ts'] ?? o['time']) ?? 0,
        arrayRelativDeg: rel,
        ...(heading !== undefined ? { headingDeg: heading } : {}),
        papr: tallEllerUndef(o['paprDb'] ?? o['conf']) ?? 0,
        frekvensHz: frek,
        ...(tallEllerUndef(o['signalDb'] ?? o['power']) !== undefined
            ? { signalDb: tallEllerUndef(o['signalDb'] ?? o['power']) }
            : {}),
        ...(tallEllerUndef(o['snrDb']) !== undefined ? { snrDb: tallEllerUndef(o['snrDb']) } : {}),
        antennegeometri: o['antennegeometri'] ?? o['antType'],
        ...(tallEllerUndef(o['lat']) !== undefined ? { lat: tallEllerUndef(o['lat']) } : {}),
        ...(tallEllerUndef(o['lon']) !== undefined ? { lon: tallEllerUndef(o['lon']) } : {}),
    }, {
        ...opts,
        ...(o['algoritme'] !== undefined && opts.algoritme === undefined
            ? { algoritme: KrakenDoaAlgoritme.parse(o['algoritme']) }
            : {}),
        ...(tallEllerUndef(o['bandbreddeHz']) !== undefined && opts.bandbreddeHz === undefined
            ? { bandbreddeHz: tallEllerUndef(o['bandbreddeHz']) }
            : {}),
    });
    if (p && eksplisittSann && p.referanse === 'array-relativ') {
        return { ...p, referanse: 'sann' };
    }
    return p;
}
/** Maks linjer/elementer vi prosesserer i én batch (DoS-vern, jf. spark.ts). */
export const KRAKEN_MAKS_BATCH = 2000;
/**
 * Parse en hel utlesning. «Kraken App» skriver ÉN LINJE PER VFO adskilt med `\n`
 * [S6] — en post som vokter fire bånd gir fire peilinger i samme HTTP-svar, og de
 * skal bli fire objekter, ikke ett.
 */
export function parseKrakenDoaBatch(raw, opts = {}) {
    const ut = [];
    const push = (v) => {
        const p = parseKrakenDoa(v, opts);
        if (p)
            ut.push(p);
    };
    if (typeof raw === 'string') {
        for (const l of raw.split('\n').slice(0, KRAKEN_MAKS_BATCH)) {
            if (l.trim().length > 0)
                push(l);
        }
        return ut;
    }
    if (Array.isArray(raw)) {
        for (const v of raw.slice(0, KRAKEN_MAKS_BATCH))
            push(v);
        return ut;
    }
    push(raw);
    return ut;
}
/**
 * Peiling → peilelinje. Returnerer null når vi ikke vet hvor posten står, eller
 * når peilingen er array-relativ: en linje tegnet fra en ukjent referanseretning
 * er en løgn med kartografisk troverdighet, og det er verre enn ingenting.
 */
export function krakenPeilelinje(p, opts = {}) {
    const lat = p.lat ?? opts.post?.lat;
    const lon = p.lon ?? opts.post?.lon;
    if (lat === undefined || lon === undefined)
        return null;
    if (p.referanse !== 'sann')
        return null;
    const fra = { lat, lon };
    const lengdeM = Math.max(1, opts.lengdeM ?? 30_000);
    const kSigma = Math.max(0, opts.kSigma ?? 2);
    const sigmaDeg = opts.sigmaDeg ?? peilefeilSigmaDeg(p);
    const halv = Math.min(89, sigmaDeg * kSigma);
    const senter = project(fra, p.peilingDeg, lengdeM);
    const linje = [
        [fra.lon, fra.lat],
        [senter.lon, senter.lat],
    ];
    // Sektoren: fra posten, langs venstre kant, bue i ytterkant, tilbake langs
    // høyre kant. Buen gjøres i steg så den ikke ser ut som en trekant på zoom.
    const sektor = [[fra.lon, fra.lat]];
    const steg = 12;
    for (let i = 0; i <= steg; i++) {
        const d = -halv + (2 * halv * i) / steg;
        const q = project(fra, p.peilingDeg + d, lengdeM);
        sektor.push([q.lon, q.lat]);
    }
    sektor.push([fra.lon, fra.lat]);
    return { sensorId: p.sensorId, fra, peilingDeg: p.peilingDeg, sigmaDeg, kSigma, lengdeM, tsMs: p.tsMs, linje, sektor };
}
/** Peilelinje → GeoJSON (linje + sektor) for kartlaget. */
export function krakenPeilelinjeGeoJson(pl) {
    const props = {
        sensorId: pl.sensorId,
        peilingDeg: pl.peilingDeg,
        sigmaDeg: pl.sigmaDeg,
        tsMs: pl.tsMs,
        // Eksplisitt i dataene, ikke bare i en kommentar: kartlaget skal kunne vise
        // teksten direkte, så ingen operatør tror linja ender ved målet.
        merknad: 'peiling — kun retning, ingen avstand',
    };
    const f = [
        { type: 'Feature', geometry: { type: 'LineString', coordinates: pl.linje }, properties: { ...props, rolle: 'senterlinje' } },
        { type: 'Feature', geometry: { type: 'Polygon', coordinates: [pl.sektor] }, properties: { ...props, rolle: 'usikkerhetssektor' } },
    ];
    return featureCollection(f);
}
/**
 * Peiling → `Track`.
 *
 * ════ HVORFOR DENNE FUNKSJONEN IKKE GJØR DET DU VENTER ════
 * `Track` KREVER `lat`/`lon`. En enkelt peiling har ingen posisjon. De to kravene
 * kan ikke oppfylles samtidig uten å dikte, og vi dikter ikke:
 *
 *   Alternativ A (forkastet): plasser sporet på en «typisk» avstand. Da får
 *     operatøren et fiendtlig symbol på et sted ingen har målt. Det er den
 *     verste feilen et situasjonsbilde kan gjøre — og den overlever i minnet
 *     hos folk lenge etter at man har trukket den tilbake.
 *   Alternativ B (forkastet): utled avstand fra signalstyrken. Nivået er ukalibrert
 *     dB FS [S6], og selv kalibrert er sendereffekten ukjent. Ren gjetning.
 *   Alternativ C (valgt): sporet plasseres på POSTEN — det ENESTE punktet vi
 *     faktisk kjenner — med `kind: 'sensor'`, og peilingen som attributt. Selve
 *     retningen tegnes som peilelinje/sektor (`krakenPeilelinje`), som er en
 *     geometri kartet kan vise uten å påstå en posisjon.
 *
 * Vil du ha et MÅLSPOR, må du ha en posisjon, og en posisjon får du bare fra
 * `krysspeiling()` med to eller flere poster. Da — og bare da — finnes
 * `krysspeilingTilTrack()`.
 *
 * `id` er stabil per post (ikke per måling), så den nyeste peilingen oppdaterer
 * samme symbol i stedet for å strø kartet med historikk.
 */
export function krakenPeilingTilTrack(p, post) {
    const lat = p.lat ?? post?.lat;
    const lon = p.lon ?? post?.lon;
    if (lat === undefined || lon === undefined)
        return null;
    const mhz = (p.frekvensHz / 1e6).toFixed(3);
    const treff = peilingVaktTreff(p);
    const extra = {
        peiling: `${Math.round(p.peilingDeg)}°`,
        referanse: p.referanse === 'sann' ? 'sann peiling' : 'ARRAY-RELATIV — ubrukelig geografisk',
        frekvens: `${mhz} MHz`,
        algoritme: p.algoritme,
        antenne: p.antennegeometri,
        papr: `${p.paprDb.toFixed(1)} dB`,
        'peilefeil 1σ': `${peilefeilSigmaDeg(p).toFixed(1)}°`,
        // Står i dataene, ikke bare i koden — et UI skal kunne vise det ordrett.
        avstand: 'UKJENT — peiling gir kun retning',
    };
    if (p.signalDb !== undefined)
        extra['nivå'] = `${p.signalDb.toFixed(1)} dB FS`;
    if (p.snrDb !== undefined)
        extra['snr'] = `${p.snrDb.toFixed(1)} dB`;
    if (p.bandbreddeHz !== undefined)
        extra['båndbredde'] = `${Math.round(p.bandbreddeHz / 1000)} kHz`;
    if (treff.length)
        extra['vaktliste'] = treff.map((t) => t.navn).join(', ');
    if (p.adcOverdrive)
        extra['advarsel'] = 'ADC-metning — peilingen er mistenkelig';
    return {
        id: `kraken:${p.sensorId}`,
        // 'sensor', ikke 'detection': symbolet ER posten. Deteksjonen er retningen,
        // og den bor i peilelinja.
        kind: 'sensor',
        affiliation: 'friend',
        lat,
        lon,
        sidc: 'SFGPEWS----', // vennlig sensor — samme som SPARK-noden i adapters.ts
        label: `${post?.navn ?? p.sensorId} — RF ${Math.round(p.peilingDeg)}° @ ${mhz} MHz`,
        tsMs: p.tsMs,
        confidence: p.konfidens,
        source: `krakenrf:${p.sensorId}`,
        // Postens posisjon er MÅLT, ikke estimert. Ingen `cepM`: den ville ellers
        // blitt lest som målusikkerhet for noe det ikke finnes noe mål for.
        estimated: false,
        extra,
    };
}
/** Default 1σ når kalleren ikke oppgir noe. UVERIFISERT, se `PEILEFEIL_BASIS_DEG`. */
export const KRYSSPEILING_STANDARD_SIGMA_DEG = 5;
/** Kryssvinkel-terskler. UVERIFISERT: driftsvalg, ikke fysikk. */
export const KRYSSVINKEL_GOD_DEG = 30;
export const KRYSSVINKEL_BRUKBAR_DEG = 15;
export const KRYSSVINKEL_DARLIG_DEG = 5;
function klassifiserGeometri(besteKryssvinkelDeg) {
    if (besteKryssvinkelDeg >= KRYSSVINKEL_GOD_DEG)
        return 'god';
    if (besteKryssvinkelDeg >= KRYSSVINKEL_BRUKBAR_DEG)
        return 'brukbar';
    if (besteKryssvinkelDeg >= KRYSSVINKEL_DARLIG_DEG)
        return 'darlig';
    return 'ubrukelig';
}
/**
 * Sfærisk skjæring mellom to peilelinjer. Hver peiling definerer en storsirkel
 * med normal `p × d`; skjæringen er `±(N1 × N2)`. Av de to antipodale kandidatene
 * beholdes bare den som ligger FRAMOVER langs BEGGE peilingene: for et punkt
 * `c = p·cos s + d·sin s` er `d · c = sin s`, så `d · c > 0` ⟺ `0 < s < π`.
 * Det er nettopp den testen som skiller en peiling fra dens bakpeiling.
 */
function skjaering2(a, b) {
    const pa = tilVektor(a);
    const pb = tilVektor(b);
    const da = retningsvektor(a, a.peilingDeg);
    const db = retningsvektor(b, b.peilingDeg);
    const na = normaliserVektor(kryss(pa, da));
    const nb = normaliserVektor(kryss(pb, db));
    if (!na || !nb)
        return null;
    const l = normaliserVektor(kryss(na, nb));
    if (!l)
        return null; // parallelle storsirkler — ingen entydig skjæring
    for (const c of [l, [-l[0], -l[1], -l[2]]]) {
        if (prikk(da, c) > 0 && prikk(db, c) > 0)
            return fraVektor(c);
    }
    return null; // divergerende: ingen kandidat ligger framover for begge
}
/**
 * Skjæringspunkt mellom to eller flere peilinger fra ULIKE poster, med feilellipse.
 *
 * Framgangsmåte:
 *  1. Valider: ≥ 2 peilinger, ≥ 2 ULIKE poster, basislinje > `minBaselineM`.
 *  2. Startgjett fra det best konditionerte paret (største kryssvinkel), sfærisk.
 *  3. Gauss-Newton på vinkelresidualene, vektet med 1/σ². Residualene regnes
 *     SFÆRISK (`initialPeilingDeg`); bare parametriseringen er et lokalt
 *     tangentplan, så løsningen er den riktige selv om jacobianen er tilnærmet.
 *  4. Kovarians `(BᵀWB)⁻¹` → feilellipse via egenverdiene til en 2×2.
 *  5. Forkast: ingen framoverskjæring, utenfor `maksAvstandM`, eller residualer
 *     som viser at peilingene ikke ser samme sender.
 *
 * Returnerer null i stedet for et falskt punkt. Nesten-parallelle peilinger
 * returneres MED løsning, men med `geometri: 'ubrukelig'`, stor ellipse og
 * `advarsel` — de er informasjon, ikke en posisjon.
 */
export function krysspeiling(inn, opts = {}) {
    const standardSigma = opts.standardSigmaDeg ?? KRYSSPEILING_STANDARD_SIGMA_DEG;
    const minBaseline = opts.minBaselineM ?? 100;
    const maksAvstand = opts.maksAvstandM ?? 200_000;
    const b = inn.filter((x) => x &&
        typeof x.sensorId === 'string' &&
        x.sensorId.length > 0 &&
        Number.isFinite(x.lat) &&
        Number.isFinite(x.lon) &&
        Number.isFinite(x.peilingDeg) &&
        Math.abs(x.lat) <= 90 &&
        Math.abs(x.lon) <= 180).map((x) => ({
        ...x,
        peilingDeg: normaliserGrader(x.peilingDeg),
        sigmaDeg: Math.max(0.01, x.sigmaDeg ?? standardSigma),
    }));
    if (b.length < 2)
        return null;
    // Krav: ULIKE poster. To peilinger fra samme antenne krysser aldri noe.
    if (new Set(b.map((x) => x.sensorId)).size < 2)
        return null;
    // Krav: reell basislinje. Vi ser på den STØRSTE — finnes én god basislinje,
    // holder det, selv om to av postene står tett.
    let maksBaseline = 0;
    for (let i = 0; i < b.length; i++) {
        for (let j = i + 1; j < b.length; j++) {
            if (b[i].sensorId === b[j].sensorId)
                continue;
            maksBaseline = Math.max(maksBaseline, haversineM(b[i], b[j]));
        }
    }
    if (maksBaseline < minBaseline)
        return null;
    // Kryssvinkler (bare mellom ULIKE poster).
    let beste = -1;
    let minKryss = 90;
    let bestePar = null;
    for (let i = 0; i < b.length; i++) {
        for (let j = i + 1; j < b.length; j++) {
            if (b[i].sensorId === b[j].sensorId)
                continue;
            const v = kryssvinkelDeg(b[i].peilingDeg, b[j].peilingDeg);
            minKryss = Math.min(minKryss, v);
            if (v > beste) {
                beste = v;
                bestePar = [b[i], b[j]];
            }
        }
    }
    if (!bestePar || beste < 0)
        return null;
    // Startgjett: sfærisk skjæring av det best konditionerte paret. Er DEN
    // divergerende, prøver vi de øvrige parene før vi gir opp — ett dårlig par skal
    // ikke velte en løsning tre andre peilinger er enige om.
    let start = skjaering2(bestePar[0], bestePar[1]);
    if (!start) {
        for (let i = 0; i < b.length && !start; i++) {
            for (let j = i + 1; j < b.length && !start; j++) {
                if (b[i].sensorId === b[j].sensorId)
                    continue;
                start = skjaering2(b[i], b[j]);
            }
        }
    }
    if (!start)
        return null; // divergerende peilinger — ingen skjæring framover
    // ── Gauss-Newton ──────────────────────────────────────────────────────────
    const ref = start;
    const poster = b.map((x) => tilLokal(ref, x));
    let e = 0;
    let n = 0;
    let siste = null;
    for (let iter = 0; iter < 12; iter++) {
        const x = fraLokal(ref, e, n);
        let m11 = 0;
        let m12 = 0;
        let m22 = 0;
        let g1 = 0;
        let g2 = 0;
        let gyldige = 0;
        for (let i = 0; i < b.length; i++) {
            const dE = e - poster[i].e;
            const dN = n - poster[i].n;
            const r2 = dE * dE + dN * dN;
            if (r2 < 1)
                continue; // punktet ligger praktisk talt på posten
            const rest = peilingDiffDeg(b[i].peilingDeg, initialPeilingDeg(b[i], x));
            const w = 1 / (b[i].sigmaDeg * b[i].sigmaDeg);
            const be = (dN / r2) * R2D;
            const bn = (-dE / r2) * R2D;
            m11 += w * be * be;
            m12 += w * be * bn;
            m22 += w * bn * bn;
            g1 += w * be * rest;
            g2 += w * bn * rest;
            gyldige++;
        }
        if (gyldige < 2)
            return null;
        siste = { m11, m12, m22 };
        const d = loes2x2(m11, m12, m12, m22, g1, g2);
        if (!d)
            return null; // singulær normalmatrise = parallelle peilinger
        // Demp store steg: nesten-parallell geometri kan ellers kaste løsningen
        // tusenvis av kilometer i én iterasjon og aldri komme tilbake.
        const lengde = Math.hypot(d[0], d[1]);
        const maksSteg = 5 * maksAvstand;
        const skala = lengde > maksSteg ? maksSteg / lengde : 1;
        e += d[0] * skala;
        n += d[1] * skala;
        if (lengde * skala < 0.05)
            break;
    }
    if (!siste)
        return null;
    const losning = fraLokal(ref, e, n);
    if (!Number.isFinite(losning.lat) || !Number.isFinite(losning.lon))
        return null;
    if (Math.abs(losning.lat) > 90)
        return null;
    // Avstander + residualer på den ferdige løsningen.
    const avstanderM = b.map((x) => haversineM(x, losning));
    const rester = b.map((x) => Math.abs(peilingDiffDeg(x.peilingDeg, initialPeilingDeg(x, losning))));
    const maksRestDeg = Math.max(...rester);
    if (Math.max(...avstanderM) > maksAvstand)
        return null; // divergerende/artefakt
    const restTak = opts.maksRestDeg ?? 3 * Math.max(...b.map((x) => x.sigmaDeg));
    if (maksRestDeg > restTak)
        return null; // peilingene ser ikke samme sender
    // ── Feilellipse fra kovariansen (BᵀWB)⁻¹ ──────────────────────────────────
    const det = siste.m11 * siste.m22 - siste.m12 * siste.m12;
    let semiMajorM = Infinity;
    let semiMinorM = Infinity;
    let orienteringDeg = 0;
    if (Number.isFinite(det) && Math.abs(det) > 1e-30) {
        const cee = siste.m22 / det;
        const cnn = siste.m11 / det;
        const cen = -siste.m12 / det;
        const halvSpor = (cee + cnn) / 2;
        const disc = Math.sqrt(Math.max(0, ((cee - cnn) / 2) ** 2 + cen * cen));
        const l1 = halvSpor + disc;
        const l2 = Math.max(0, halvSpor - disc);
        semiMajorM = Math.sqrt(Math.max(0, l1));
        semiMinorM = Math.sqrt(l2);
        // Egenvektor for største egenverdi: (cen, l1 − cee) i (øst, nord) → asimut.
        orienteringDeg = normaliserGrader(Math.atan2(cen, l1 - cee) * R2D) % 180;
    }
    const cepM = Number.isFinite(semiMajorM) ? 0.589 * (semiMajorM + semiMinorM) : Infinity;
    const geometri = klassifiserGeometri(beste);
    let advarsel;
    if (geometri === 'ubrukelig') {
        advarsel = `nesten parallelle peilinger (beste kryssvinkel ${beste.toFixed(1)}°) — posisjonen er retningsgivende, ikke en fatning`;
    }
    else if (geometri === 'darlig') {
        advarsel = `svak geometri (beste kryssvinkel ${beste.toFixed(1)}°) — stor usikkerhet langs peileretningen`;
    }
    else if (maksRestDeg > Math.max(...b.map((x) => x.sigmaDeg))) {
        advarsel = `peilingene er uenige (maks rest ${maksRestDeg.toFixed(1)}°) — kan være to sendere`;
    }
    return {
        lat: losning.lat,
        lon: losning.lon,
        antallPeilinger: b.length,
        sensorIder: b.map((x) => x.sensorId),
        besteKryssvinkelDeg: beste,
        minKryssvinkelDeg: minKryss,
        geometri,
        ellipse: { semiMajorM, semiMinorM, orienteringDeg },
        cepM,
        maksRestDeg,
        avstanderM,
        tsMs: Math.max(0, ...b.map((x) => x.tsMs ?? 0)),
        ...(advarsel ? { advarsel } : {}),
    };
}
/**
 * `KrakenPeiling` → `KrysspeilingInn`. Nekter (null) for array-relative peilinger:
 * å krysse to peilinger med ukjent referanseretning gir et presist tall som ikke
 * betyr noe. Bedre å mangle fatningen enn å tro på den.
 */
export function krysspeilingInn(p, post) {
    if (p.referanse !== 'sann')
        return null;
    const lat = p.lat ?? post?.lat;
    const lon = p.lon ?? post?.lon;
    if (lat === undefined || lon === undefined)
        return null;
    return {
        sensorId: p.sensorId,
        lat,
        lon,
        peilingDeg: p.peilingDeg,
        sigmaDeg: peilefeilSigmaDeg(p),
        tsMs: p.tsMs,
    };
}
/**
 * Krysspeiling → `Track`. HER er et målspor legitimt: punktet er faktisk løst av
 * to eller flere uavhengige målinger, og `cepM` sier hvor godt.
 *
 * `affiliation` er `unknown`, ikke `hostile`. En RF-emitter er en emitter — hvem
 * den tilhører er en VURDERING et menneske gjør, ikke noe en DoA-løsning vet.
 */
export function krysspeilingTilTrack(k, o = {}) {
    const mhz = o.frekvensHz !== undefined ? (o.frekvensHz / 1e6).toFixed(3) : undefined;
    const extra = {
        poster: k.sensorIder.join(', '),
        geometri: k.geometri,
        'kryssvinkel (beste)': `${k.besteKryssvinkelDeg.toFixed(1)}°`,
        'feilellipse 1σ': `${Math.round(k.ellipse.semiMajorM)} × ${Math.round(k.ellipse.semiMinorM)} m @ ${Math.round(k.ellipse.orienteringDeg)}°`,
        'maks rest': `${k.maksRestDeg.toFixed(2)}°`,
        metode: 'krysspeiling',
    };
    if (mhz)
        extra['frekvens'] = `${mhz} MHz`;
    if (k.advarsel)
        extra['advarsel'] = k.advarsel;
    return {
        id: o.id ?? `kraken-fix:${k.sensorIder.join('+')}${mhz ? `:${mhz}` : ''}`,
        kind: 'detection',
        affiliation: 'unknown',
        lat: k.lat,
        lon: k.lon,
        // Ukjent bakke-EW-emitter. Bare tilhørighet (U) og EW-familien påstås;
        // undertypen står som «-» fordi vi ikke vet hva senderen er.
        sidc: 'SUGPEW-----',
        label: o.etikett ?? `RF-FATNING${mhz ? ` ${mhz} MHz` : ''}`,
        tsMs: k.tsMs,
        cepM: Number.isFinite(k.cepM) ? k.cepM : undefined,
        source: `krakenrf:krysspeiling:${k.sensorIder.join('+')}`,
        // Estimert: løst av peilinger, ikke direkte målt posisjon.
        estimated: true,
        extra,
    };
}
/** Feilellipsen som lukket ring ([lon, lat]-par), til kartlaget. */
export function krysspeilingEllipseRing(k, kSigma = 1, steg = 48) {
    if (!Number.isFinite(k.ellipse.semiMajorM) || !Number.isFinite(k.ellipse.semiMinorM))
        return [];
    const a = k.ellipse.semiMajorM * kSigma;
    const bb = k.ellipse.semiMinorM * kSigma;
    const senter = { lat: k.lat, lon: k.lon };
    const ring = [];
    for (let i = 0; i <= steg; i++) {
        const t = (2 * Math.PI * i) / steg;
        // Parametrisk ellipse i (storakse, lilleakse), rotert til asimut.
        const langs = a * Math.cos(t);
        const tvers = bb * Math.sin(t);
        const r = Math.hypot(langs, tvers);
        const vinkel = k.ellipse.orienteringDeg + Math.atan2(tvers, langs) * R2D;
        const p = project(senter, vinkel, r);
        ring.push([p.lon, p.lat]);
    }
    return ring;
}
//# sourceMappingURL=krakenrf.js.map